import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the LeavePolicyMasterContext
const LeavePolicyMasterContext = createContext();

// Custom hook to use the LeavePolicyMasterContext
export const useLeavePolicy = () => useContext(LeavePolicyMasterContext);

// Provider component to wrap the app
export const LeavePolicyMasterProvider = ({ children }) => {
  const [policies, setPolicies] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/policies"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPolicies = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setPolicies(response.data?.data || {}); // Access `data.data` for actual policy master
      const { data, current_page, last_page, per_page, total } = response.data;

      setPolicies(data.data || []); // Update policies list

      setPagination({
        currentPage: response.data.data.current_page,
        totalPages: response.data.data.last_page,
        totalItems: response.data.data.total,
        perPage: response.data.data.per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching policies master:", error);
      setError(error.response?.data?.message || "Failed to fetch policies.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch policies master from the API on load
  useEffect(() => {
    fetchPolicies();
  }, [API_URL, token]);

  // Add a new policy
  const addPolicy = async (newPolicy) => {
    try {
      const response = await axios.post(API_URL, newPolicy, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPolicies((prev) => [response.data?.data, ...prev]);
      return { success: true, data: response.data?.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add policy.";
      const errors = error.response?.data?.errors || null;
      return { success: false, message, errors };
    }
  };

  // Update an existing policy
  const updatePolicy = async (updatedPolicy) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedPolicy.id}`,
        updatedPolicy,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setPolicies((prev) =>
        prev.map((policy) =>
          policy.id === updatedPolicy.id
            ? { ...policy, ...response.data?.data }
            : policy,
        ),
      );
      return { success: true, data: response.data?.data };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update policy.";
      const errors = error.response?.data?.errors || null;
      return { success: false, message, errors };
    }
  };

  // Delete a policy
  const deletePolicy = async (id, body = {}) => {
    try {
      const response = await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: body, // optional reason supported by backend
      });
      const deleted = response.data?.data || null;
      if (deleted) {
        setPolicies((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...deleted } : p)),
        );
      } else {
        // Fallback: mark as trashed if backend doesn't return updated entity
        setPolicies((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, deleted_at: new Date().toISOString() } : p,
          ),
        );
      }
      return { success: true, data: deleted };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete policy.";
      const errors = error.response?.data?.errors || null;
      return { success: false, message, errors };
    }
  };

  // Restore a soft-deleted policy
  const restorePolicy = async (id, body = {}) => {
    try {
      const response = await axios.post(`${API_URL}/${id}/restore`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const restored = response.data?.data || null;
      if (restored) {
        setPolicies((prev) => {
          const others = prev.filter((p) => p.id !== id);
          return [restored, ...others];
        });
      }
      return { success: true, data: restored };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to restore policy.";
      const errors = error.response?.data?.errors || null;
      return { success: false, message, errors };
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      policies,
      fetchPolicies,
      addPolicy,
      updatePolicy,
      deletePolicy,
      restorePolicy,
      fetchPolicyAudits: async (policyId, { page = 1, per_page = 10 } = {}) => {
        try {
          const response = await axios.get(`${API_URL}/${policyId}/audits`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: { page, per_page },
          });
          const {
            data,
            current_page,
            last_page,
            per_page: pp,
            total,
          } = response.data || {};
          const items = data?.data || [];
          return {
            success: true,
            data: items,
            pagination: {
              currentPage: current_page,
              totalPages: last_page,
              totalItems: total,
              perPage: pp,
            },
          };
        } catch (error) {
          const message =
            error.response?.data?.message || "Failed to fetch audits.";
          const errors = error.response?.data?.errors || null;
          return { success: false, message, errors };
        }
      },
    }),
    [policies, isLoading], // Recalculate the value only when `policies` changes
  );

  return (
    <LeavePolicyMasterContext.Provider value={value}>
      {children}
    </LeavePolicyMasterContext.Provider>
  );
};
